import {
  PollResultsModeType,
  type CreatePollInputType,
  type UpdatePollInputType,
} from "@/types/graphql/partner.generated";
import type { LibraryPoll } from "@/partner/entities/polls";

/** Пределы совпадают с бэкендом (PollValidator). */
export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 6;
export const POLL_MAX_QUESTION_LENGTH = 200;
export const POLL_MAX_OPTION_LENGTH = 100;

export interface PollOptionFormValue {
  /** id варианта, который уже есть в опросе; null у нового. */
  id: string | null;
  text: string;
  isCorrect: boolean;
}

export interface PollLibraryFormValues {
  question: string;
  options: PollOptionFormValue[];
  isMultiple: boolean;
  resultsMode: PollResultsModeType;
}

export const POLL_RESULTS_MODE_LABEL_KEYS: Record<PollResultsModeType, string> =
  {
    [PollResultsModeType.AfterVoteType]: "polls.resultsMode.afterVote",
    [PollResultsModeType.AfterCloseType]: "polls.resultsMode.afterClose",
  };

export type PollLibraryFormError =
  | "questionRequired"
  | "questionTooLong"
  | "optionsCount"
  | "optionRequired"
  | "optionTooLong";

export const POLL_LIBRARY_FORM_ERROR_KEYS: Record<
  PollLibraryFormError,
  string
> = {
  questionRequired: "polls.errors.questionRequired",
  questionTooLong: "polls.errors.questionTooLong",
  optionsCount: "polls.errors.optionsCount",
  optionRequired: "polls.errors.optionRequired",
  optionTooLong: "polls.errors.optionTooLong",
};

export function emptyPollLibraryForm(): PollLibraryFormValues {
  return {
    question: "",
    options: [
      { id: null, text: "", isCorrect: false },
      { id: null, text: "", isCorrect: false },
    ],
    isMultiple: false,
    resultsMode: PollResultsModeType.AfterVoteType,
  };
}

export function pollToLibraryFormValues(
  poll: Pick<LibraryPoll, "question" | "options" | "isMultiple" | "resultsMode">
): PollLibraryFormValues {
  return {
    question: poll.question,
    options: poll.options.map((option) => ({
      id: option.id,
      text: option.text,
      isCorrect: option.isCorrect,
    })),
    isMultiple: poll.isMultiple,
    resultsMode: poll.resultsMode,
  };
}

export function validatePollLibraryForm(
  values: PollLibraryFormValues
): PollLibraryFormError | null {
  const question = values.question.trim();
  if (!question) return "questionRequired";
  if (question.length > POLL_MAX_QUESTION_LENGTH) return "questionTooLong";
  if (
    values.options.length < POLL_MIN_OPTIONS ||
    values.options.length > POLL_MAX_OPTIONS
  ) {
    return "optionsCount";
  }
  for (const option of values.options) {
    const text = option.text.trim();
    if (!text) return "optionRequired";
    if (text.length > POLL_MAX_OPTION_LENGTH) return "optionTooLong";
  }
  return null;
}

export function toCreatePollInput(
  values: PollLibraryFormValues
): CreatePollInputType {
  return {
    question: values.question.trim(),
    options: values.options.map((option) => ({
      id: option.id,
      text: option.text.trim(),
      isCorrect: option.isCorrect,
    })),
    isMultiple: values.isMultiple,
    resultsMode: values.resultsMode,
  };
}

/**
 * У опроса с ответами варианты, множественный выбор и режим результатов
 * заблокированы (бэкенд ответит POLL_LOCKED), поэтому уходит только вопрос.
 */
export function toUpdatePollInput(
  values: PollLibraryFormValues,
  locked: boolean
): UpdatePollInputType {
  if (locked) return { question: values.question.trim() };
  return toCreatePollInput(values);
}
